import  { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "react-toastify/dist/ReactToastify.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocalStorage } from "@uidotdev/usehooks";
import TextEditor from "../Components/TextEditor/TextEditor";
import { DialogActions, DialogContent, DialogContentText, FormControlLabel, Switch } from "@mui/material";
import { ArrowBackIosNewRounded } from "@mui/icons-material";
import isDesktop from "../isDesktop";
import { hasStoredTabAccess } from '../utils/access';

const Skills = () => {
  const [skills, setSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [missionSearchTerm, setMissionSearchTerm] = useState("");
  const [missionExactMatch, setMissionExactMatch] = useState(false);
  const [missionSearchResults, setMissionSearchResults] = useState([]);
  const [missionSearchMeta, setMissionSearchMeta] = useState(null);
  const [missionSearchLoading, setMissionSearchLoading] = useState(false);
  const [missionSearchError, setMissionSearchError] = useState("");
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsError, setSkillsError] = useState("");
  const latestFetchId = useRef(0);
  const latestMissionSearchId = useRef(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [replicateDialogOpen, setReplicateDialogOpen] = useState(false);
  const [replicateSkillId, setReplicateSkillId] = useState(null);
  const [replicateSkillName, setReplicateSkillName] = useState("");
  const [replicateSkillOrder, setReplicateSkillOrder] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  const navigate=useNavigate()
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktopDevice = isDesktop();
  const [newSkill, setNewSkill] = useState({
    name: "",
    skillRetry:false,
    fee: 0,
    discountedFee: 0,
    description: "",
    skillOrder: 0,
    paymentLink: "",
  });
  const [editSkill, setEditSkill] = useState({
    id: null,
    name: "",
    skillRetry:false,
    fee: 0,
    discountedFee: 0,
    description: "",
    skillOrder: 0,
    paymentLink: "",
  });

  // eslint-disable-next-line no-unused-vars
  const [token, setToken] = useLocalStorage("token", null);
  const isAuthorized = hasStoredTabAccess('Skills');

  useEffect(() => {
    const missionSearch = searchParams.get("missionSearch") || "";
    const exactMatch = searchParams.get("exact") === "true";

    setMissionSearchTerm(missionSearch);
    setMissionExactMatch(exactMatch);
  }, [searchParams]);

  const handleEdit = (skill) => {
    setEditSkill({...skill,skillRetry:skill.can_retry});
    setEditDialogOpen(true);
  };

  const handleEditSkills = async (skillsId) => {
    try {
      const requestData = {
        skillName: editSkill.skill_name,
        skillRetry:editSkill.skillRetry,
        description: editSkill.description,
        skillFee: editSkill.skill_fee,
        discountedFee: editSkill.discounted_fee,
        skillOrder: editSkill.skill_order,
        paymentLink: editSkill.payment_link,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/skills/update/${skillsId}`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success(`${response.data.message}`);
        handleEditDialogClose();
        fetchSkills(searchTerm);
      } else {
        toast.error(`Error updating skill: ${response.data.message}`);
      }
    } catch (error) {
      toast.error(
        `Error updating skill: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditSkill({
      id: null,
      name: "",
      skillRetry:false,
      fee: 0,
      discountedFee: 0,
      description: "",
      skillOrder: 0,
      paymentLink: "",
    });
  };

  const handleConfirmDelete = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/skills/delete/${skillToDelete}`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.delete(apiUrl, config);

      if (response.status === 200) {
        console.log("Skill deleted successfully");
        fetchSkills(searchTerm);
        toast.success("Skill deleted successfully");
      } else {
        console.error("Error deleting skill: ", response.data.message);
        toast.error(`Error deleting skill: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Error deleting skill: ", error);
      toast.error(`Error deleting skill: ${error.message}`);
    }
    setSkillToDelete(null);
    setDeleteOpen(false);
  };

  const handleDelete = (skillsId) => {
    setDeleteOpen(true);
    setSkillToDelete(skillsId);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewSkill({
      name: "",
      fee: 0,
      discountedFee: 0,
      description: "",
      skillOrder: 0,
      paymentLink: "",
    });
  };

  const handleAddNewSkills = async () => {
    try {
      const requestData = {
        skillName: newSkill.name,
        skillRetry:newSkill.skillRetry,
        description: newSkill.description,
        skillFee: newSkill.fee,
        discountedFee: newSkill.discountedFee,
        skillOrder: newSkill.skillOrder,
        paymentLink: newSkill.paymentLink,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/skills/add`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201) {
        toast.success(`${response.data.message}`);
        handleCloseDialog();
        fetchSkills(searchTerm);
      } else {
        toast.error(`${response.data.message}`);
      }
    } catch (error) {
      toast.error(`${error.response.data.message}`);
    }
  };

  const fetchSkills = useCallback(async (searchValue = "") => {
    const fetchId = latestFetchId.current + 1;
    latestFetchId.current = fetchId;
    const trimmedSearch = searchValue.trim();
    const searchQuery = trimmedSearch
      ? `?search=${encodeURIComponent(trimmedSearch)}`
      : "";

    setSkillsLoading(true);
    setSkillsError("");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/skills/all${searchQuery}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (fetchId !== latestFetchId.current) {
        return;
      }

      if (response.status === 200) {
        setSkills(response.data.data || []);
      } else {
        console.error("Error fetching skills: ", response.data.message);
        setSkills([]);
        setSkillsError(response.data.message || "Error fetching skills");
      }
    } catch (error) {
      if (fetchId !== latestFetchId.current) {
        return;
      }

      console.error("Error fetching skills: ", error);
      setSkills([]);
      setSkillsError(error.response?.data?.message || "Error fetching skills");
    } finally {
      if (fetchId === latestFetchId.current) {
        setSkillsLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchSkills(searchTerm);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [fetchSkills, searchTerm]);

  const updateMissionSearchParams = useCallback(
    (searchValue, exactMatch) => {
      const nextParams = new URLSearchParams(searchParams);
      const trimmedSearch = searchValue.trim();

      if (trimmedSearch) {
        nextParams.set("missionSearch", trimmedSearch);
      } else {
        nextParams.delete("missionSearch");
      }

      if (exactMatch) {
        nextParams.set("exact", "true");
      } else {
        nextParams.delete("exact");
      }

      if (nextParams.toString() !== searchParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      }
    },
    [searchParams, setSearchParams]
  );

  const fetchMissionSearchResults = useCallback(
    async (searchValue, exactMatch) => {
      const trimmedSearch = searchValue.trim();
      const fetchId = latestMissionSearchId.current + 1;
      latestMissionSearchId.current = fetchId;

      if (!trimmedSearch) {
        setMissionSearchResults([]);
        setMissionSearchMeta(null);
        setMissionSearchError("");
        setMissionSearchLoading(false);
        return;
      }

      const params = new URLSearchParams();

      if (exactMatch) {
        params.set("missionName", trimmedSearch);
        params.set("exact", "true");
      } else {
        params.set("search", trimmedSearch);
      }

      setMissionSearchLoading(true);
      setMissionSearchError("");

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/skills/missions/search?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (fetchId !== latestMissionSearchId.current) {
          return;
        }

        if (response.status === 200) {
          setMissionSearchResults(response.data.data || []);
          setMissionSearchMeta(response.data.meta || null);
        } else {
          setMissionSearchResults([]);
          setMissionSearchMeta(null);
          setMissionSearchError(
            response.data.message || "Error fetching mission search results"
          );
        }
      } catch (error) {
        if (fetchId !== latestMissionSearchId.current) {
          return;
        }

        setMissionSearchResults([]);
        setMissionSearchMeta(null);
        setMissionSearchError(
          error.response?.data?.message || "Error fetching mission search results"
        );
      } finally {
        if (fetchId === latestMissionSearchId.current) {
          setMissionSearchLoading(false);
        }
      }
    },
    [token]
  );

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchMissionSearchResults(missionSearchTerm, missionExactMatch);
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [fetchMissionSearchResults, missionExactMatch, missionSearchTerm]);

  const handleMissionSearchChange = (event) => {
    const value = event.target.value;
    setMissionSearchTerm(value);
    updateMissionSearchParams(value, missionExactMatch);
  };

  const handleMissionExactMatchChange = (event) => {
    const checked = event.target.checked;
    setMissionExactMatch(checked);
    updateMissionSearchParams(missionSearchTerm, checked);
  };

  const formatUpdatedAt = (updatedAt) => {
    if (!updatedAt) {
      return "";
    }

    const date = new Date(updatedAt);

    if (Number.isNaN(date.getTime())) {
      return updatedAt;
    }

    return date.toLocaleString();
  };

  const handleOpenMissionEdit = (mission) => {
    if (!mission?.mission_id || !mission?.skill_id || !mission?.stage_id) {
      toast.error("Mission edit path is missing required ids.");
      return;
    }

    const returnTo = `${location.pathname}${location.search}`;
    const editParams = new URLSearchParams({
      editMissionId: String(mission.mission_id),
      returnTo,
    });

    navigate(
      `/skills/${mission.skill_id}/stage/${mission.stage_id}/mission?${editParams.toString()}`
    );
  };

  const handleReplicate = (skillId) => {
    setReplicateSkillId(skillId);
    setReplicateDialogOpen(true);
  };

  const handleReplicateDialogClose = () => {
    setReplicateDialogOpen(false);
    setReplicateSkillName("");
  };

  const handleReplicateSubmit = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/replicate/skills/${replicateSkillId}`,
        { skillName: replicateSkillName, skillOrder: replicateSkillOrder },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success(`${response.data.message}`);
        handleReplicateDialogClose();
        fetchSkills(searchTerm);
      } else {
        toast.error(`${response.data.message}`);
      }
    } catch (error) {
      toast.error(`${error.response.data.message}`);
    }
  };


  const onDescriptionChange = (value) => {
    setEditSkill((prev) => ({
      ...prev,
      description: value,
    }));
  };

  console.log(editSkill.description);
  const missionResultCount =
    missionSearchMeta?.count ?? missionSearchResults.length;

  return (
    <div className="container-fluid mt-5 p-0">
      <div className="align-center-all full-height">
        {isAuthorized ? (
          <Card sx={{ boxShadow: "none" }}>
            <CardContent>
              <ToastContainer />
              <div className="top-div mt-5">
              <div
                style={{
                  display: "flex",
                  gap: "1vw",
                  alignItems: "center",
                }}
              >
                <ArrowBackIosNewRounded
                  onClick={() => {
                    navigate("/dashboard");
                  }}
                  style={{ cursor: "pointer" }}
                />
                <h2
                  style={{ marginRight: "1rem", marginBottom: "0px" }}
                  className="page-header"
                >
                  Skills
                </h2>
              </div>
                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  {isDesktopDevice && (
                    <>
                      <TextField
                        label="Search skills"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        className="customTextfield"
                        sx={{
                          width: "14rem !important",
                          marginBottom: "0 !important",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "transparent",
                            },
                            "&:hover fieldset": {
                              borderColor: "transparent",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "transparent",
                            },
                          },
                        }}
                      />
                      <TextField
                        label="Search missions by name"
                        value={missionSearchTerm}
                        onChange={handleMissionSearchChange}
                        size="small"
                        className="customTextfield"
                        sx={{
                          width: "22rem !important",
                          marginBottom: "0 !important",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "transparent",
                            },
                            "&:hover fieldset": {
                              borderColor: "transparent",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "transparent",
                            },
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={missionExactMatch}
                            onChange={handleMissionExactMatchChange}
                            color="primary"
                          />
                        }
                        label="Exact match"
                        sx={{ marginRight: 0 }}
                      />
                    </>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleOpenDialog}
                    className="mr-2 filled-button"
                    style={{ float: "right" }}
                    sx={isDesktopDevice ? { width: "8rem !important" } : {}}
                  >
                    Add
                  </Button>
                </div>
              </div>
              {!isDesktopDevice && (
                <TextField
                  label="Search skills"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  fullWidth
                  className="customTextfieldMobile"
                  style={{ marginBottom: "0.75rem" }}
                  sx={{
                    marginBottom: "0.75rem",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
              )}
              {!isDesktopDevice && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                    marginBottom: "1rem",
                  }}
                >
                  <TextField
                    label="Search missions by name"
                    value={missionSearchTerm}
                    onChange={handleMissionSearchChange}
                    size="small"
                    fullWidth
                    className="customTextfieldMobile"
                    sx={{
                      width: "100% !important",
                      marginBottom: "0 !important",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={missionExactMatch}
                        onChange={handleMissionExactMatchChange}
                        color="primary"
                      />
                    }
                    label="Exact match"
                  />
                </div>
              )}
              {missionSearchTerm.trim() && missionSearchMeta && (
                <div
                  style={{
                    textAlign: "right",
                    fontSize: "0.875rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  {missionResultCount} result
                  {missionResultCount === 1 ? "" : "s"}
                </div>
              )}
              {missionSearchTerm.trim() ? (
                <div style={{ overflowX: "auto" }}>
                  <table className="table">
                    <thead className="table-head">
                      <tr>
                        <th className="text-left">Mission Name</th>
                        <th>Mission ID</th>
                        <th>Specialization</th>
                        <th>Skill</th>
                        <th>Stage</th>
                        <th>Mission Order</th>
                        <th>Question Type</th>
                        <th>Updated At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missionSearchLoading ? (
                        <tr>
                          <td colSpan="9" style={{ textAlign: "center" }}>
                            Loading...
                          </td>
                        </tr>
                      ) : missionSearchError ? (
                        <tr>
                          <td colSpan="9" style={{ textAlign: "center" }}>
                            {missionSearchError}
                          </td>
                        </tr>
                      ) : missionSearchResults.length === 0 ? (
                        <tr>
                          <td colSpan="9" style={{ textAlign: "center" }}>
                            No matching missions found
                          </td>
                        </tr>
                      ) : (
                        missionSearchResults.map((mission) => (
                          <tr
                            key={`${mission.mission_id}-${mission.skill_id}-${mission.stage_id}`}
                          >
                            <td className="text-left">
                              {mission.mission_name || ""}
                            </td>
                            <td>{mission.mission_id ?? ""}</td>
                            <td>{mission.specialization_name || ""}</td>
                            <td>{mission.skill_name || ""}</td>
                            <td>{mission.stage_name || ""}</td>
                            <td>{mission.mission_order ?? ""}</td>
                            <td>{mission.question_type || ""}</td>
                            <td>{formatUpdatedAt(mission.updated_at)}</td>
                            <td>
                              <Button
                                variant="contained"
                                className="g-but"
                                onClick={() => handleOpenMissionEdit(mission)}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <table className="table">
                  <thead className="table-head">
                    <tr>
                      <th>S. No</th>
                      <th className="text-left">Name</th>
                      <th>Fee</th>
                      <th>Discounted Fee</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skillsLoading ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          Loading...
                        </td>
                      </tr>
                    ) : skillsError ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          {skillsError}
                        </td>
                      </tr>
                    ) : skills.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          No skills found
                        </td>
                      </tr>
                    ) : (
                      skills.map((skill) => (
                        <tr key={skill.skill_id}>
                          <td>{skill.skill_id}</td>
                          <td className="text-left">{skill.skill_name}</td>
                          <td>{skill.skill_fee}</td>
                          <td>{skill.discounted_fee}</td>
                          <td>
                            <Button
                              variant="contained"
                              color="primary"
                              className="mr-2 g-but"
                              onClick={() => handleEdit(skill)}
                            >
                              Edit
                            </Button>
                            <Link to={`/skills/${skill.skill_id}/stage`}>
                              <Button variant="contained" className="mr-2 b-but">
                                Stage
                              </Button>
                            </Link>
                            <Button
                              variant="contained"
                              className="mr-2 y-but"
                              onClick={() => handleReplicate(skill.skill_id)}
                            >
                              Replicate
                            </Button>
                            <Button
                              aria-label="delete"
                              variant="contained"
                              className="r-but"
                              onClick={() => handleDelete(skill.skill_id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                PaperProps={{
                  sx: {
                    padding: "1vw",
                    width: "35.128vw",
                  },
                }}
              >
                <DialogTitle style={{ padding: 0, fontWeight: "600" }}>
                  Add New Skills
                </DialogTitle>
                <TextField
                  label="New Skill Name"
                  value={newSkill.name}
                  onChange={(e) =>
                    setNewSkill({ ...newSkill, name: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={newSkill.skillRetry}
                      onChange={(e) =>
                        setNewSkill({ ...newSkill, skillRetry: e.target.checked ? "1" : "0" })
                      }
                      name="Skill Retry"
                      color="primary"
                    />
                  }
                  label="Skill Retry"
                />
                <TextField
                  label="Description"
                  value={newSkill.description}
                  onChange={(e) =>
                    setNewSkill({ ...newSkill, description: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                <TextField
                  label="Fee"
                  type="number"
                  value={newSkill.fee}
                  onChange={(e) =>
                    setNewSkill({ ...newSkill, fee: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                <TextField
                  label="Discounted Fee"
                  type="number"
                  value={newSkill.discountedFee}
                  onChange={(e) =>
                    setNewSkill({
                      ...newSkill,
                      discountedFee: e.target.value,
                    })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                <TextField
                  label="Skill Order"
                  type="number"
                  value={newSkill.skillOrder}
                  onChange={(e) =>
                    setNewSkill({ ...newSkill, skillOrder: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                {/* <TextField
                    label="Payment Link"
                    value={newSkill.paymentLink}
                    onChange={(e) =>
                      setNewSkill({ ...newSkill, paymentLink: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                    className="customTextfield"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                  /> */}
                <div className="dialog-actions-box">
                  <Button
                    variant="outlined"
                    onClick={handleCloseDialog}
                    className="mt-2 mr-1 outlined-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAddNewSkills}
                    className="mt-2 filled-button"
                  >
                    Add
                  </Button>
                </div>
              </Dialog>

              <Dialog
                open={editDialogOpen}
                onClose={handleEditDialogClose}
                PaperProps={{
                  sx: {
                    padding: "1vw",
                    width: "35.128vw",
                  },
                }}
              >
                <DialogTitle style={{ padding: 0, fontWeight: "600" }}>
                  Edit Skills
                </DialogTitle>
                <TextField
                  label="Name"
                  value={editSkill.skill_name}
                  onChange={(e) =>
                    setEditSkill({ ...editSkill, skill_name: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                 <FormControlLabel
                  control={
                    <Switch
                      checked={editSkill.skillRetry}
                      onChange={(e) =>
                        setEditSkill({ ...editSkill, skillRetry: e.target.checked})
                      }
                      name="Skill Retry"
                      color="primary"
                    />
                  }
                  label="Skill Retry"
                />
                <div className="descriptionContainer">
                  <TextEditor
                    propsContent={editSkill.description}
                    propPlaceholder={"Short Description...."}
                    onChangeContent={onDescriptionChange}
                  />
                </div>
                {/* <TextField
                    label="Description"
                    value={editSkill.description}
                    onChange={(e) =>
                      setEditSkill({ ...editSkill, description: e.target.value })
                    } 
                    fullWidth
                    margin="normal"
                    className="customTextfield"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                  /> */}
              
                <TextField
                  label="Fee"
                  type="number"
                  value={editSkill.skill_fee}
                  onChange={(e) =>
                    setEditSkill({ ...editSkill, skill_fee: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                <TextField
                  label="Discounted Fee"
                  type="number"
                  value={editSkill.discounted_fee}
                  onChange={(e) =>
                    setEditSkill({
                      ...editSkill,
                      discounted_fee: e.target.value,
                    })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                <TextField
                  label="Skill Order"
                  type="number"
                  value={editSkill.skill_order}
                  onChange={(e) =>
                    setEditSkill({
                      ...editSkill,
                      skill_order: e.target.value,
                    })
                  }
                  fullWidth
                  margin="normal"
                  className="customTextfield"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                  }}
                />
                {/* <TextField
                    label="Payment Link"
                    value={editSkill.payment_link}
                    onChange={(e) =>
                      setEditSkill({
                        ...editSkill,
                        payment_link: e.target.value,
                      })
                    }
                    fullWidth
                    margin="normal"
                    className="customTextfield"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                  /> */}
                <div className="dialog-actions-box">
                  <Button
                    variant="outlined"
                    onClick={handleEditDialogClose}
                    className="mt-2 mr-1 outlined-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleEditSkills(editSkill.skill_id)}
                    className="mt-2 filled-button"
                  >
                    Save
                  </Button>
                </div>
              </Dialog>
              <Dialog
                open={replicateDialogOpen}
                onClose={handleReplicateDialogClose}
                PaperProps={{
                  sx: {
                    padding: "1vw",
                    width: "35.128vw",
                  },
                }}
              >
                <DialogTitle style={{ padding: "0 1rem", fontWeight: "600" }}>
                  Replicate Skill
                </DialogTitle>
                <form className="m-2">
                  <TextField
                    label="New Skill Name"
                    value={replicateSkillName}
                    onChange={(e) => setReplicateSkillName(e.target.value)}
                    fullWidth
                    margin="normal"
                    className="customTextfield"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                  />
                  <TextField
                    label="Skill Order"
                    type="number"
                    value={replicateSkillOrder}
                    onChange={(e) => setReplicateSkillOrder(e.target.value)}
                    fullWidth
                    margin="normal"
                    className="customTextfield"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                  />
                  <div className="dialog-actions-box mt-1">
                    <Button
                      variant="outlined"
                      onClick={handleReplicateDialogClose}
                      className="mt-2 mr-1 outlined-button"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleReplicateSubmit}
                      className="mt-2 filled-button"
                    >
                      Replicate
                    </Button>
                  </div>
                </form>
              </Dialog>
              <Dialog
                open={deleteOpen}
                onClose={(prev) => !prev}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                style={{ textAlign: "center" }}
              >
                <ErrorOutlineIcon
                  style={{
                    color: "red",
                    fontSize: "32px",
                    margin: "12px auto",
                  }}
                />
                <DialogTitle
                  id="alert-dialog-title"
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    lineHeight: "0",
                  }}
                >
                  {"Are you Sure?"}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText
                    id="alert-dialog-description"
                    style={{ fontSize: "12px" }}
                  >
                    You won't be able to revert this!
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setDeleteOpen(false);
                      setSkillToDelete(null);
                    }}
                    style={{
                      textTransform: "none",
                      width: "120px",
                      height: "36px",
                      border: "2.03px solid #37446F",
                      borderRadius: "5.85px",
                      background: "none",
                      color: "black",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmDelete}
                    style={{
                      textTransform: "none",
                      width: "120px",
                      height: "36px",
                      borderRadius: "5.85px",
                      background: "#37446F",
                      color: "white",
                    }}
                    autoFocus
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </CardContent>
          </Card>
        ) : (
          <h2>You are unauthorized to access this page.</h2>
        )}
      </div>
    </div>
  );
};

export default Skills;
